import type { AppContext } from 'types/common'
import component from './Part.svelte'
import { getSession } from 'stores/session'
import { stringToBoolean } from '@isoftdata/utility-string'
import { getObject } from '@isoftdata/utility-storage'
import { loadPartScreenData } from 'utility/load-part-screen-data'

export type { Fields, GetFieldEditabilityFn } from 'utility/load-part-screen-data'

export default function createState({ mediator, stateRouter, checkSessionPermission }: AppContext) {
	const stateName = 'app.part'

	stateRouter.addState({
		name: stateName,
		route: 'part/:storeId/:sku',
		querystringParameters: ['sku', 'storeId', 'lastSavedTime', 'lastResetTime', 'loadCachedPart'],
		defaultParameters: {
			sku: null,
			storeId() {
				return getSession()?.currentStore?.toString() ?? null
			},
			tab: 'basic',
			lastResetTime: null, // only used to trigger a state reload
			loadCachedPart: 'false',
		},
		canLeaveState(domApi) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
			return domApi.canLeaveState()
		},
		template: {
			svelte: true,
			component,
		},
		async resolve(data, { sku, tab, ...params }) {
			if (!checkSessionPermission('PARTS_CAN_VIEW_PARTS')) {
				alert('You do not have permission to view parts.')
				throw {
					redirectTo: {
						name: 'app',
					},
				}
			}
			const storeId = parseInt(params.storeId, 10)
			const loadCachedPart = stringToBoolean(params.loadCachedPart)
			const hasCachedPart = !!getObject(localStorage, 'cachedPart') || false
			// if we don't have a cached part, but we're trying to load one, don't
			if (loadCachedPart && !hasCachedPart) {
				localStorage.removeItem('cachedPart')
				localStorage.removeItem('cachedOptionValues')
				localStorage.removeItem('cachedPartAttachments')
				void mediator.publish('removeActivity', 'Unsaved Part')
				throw {
					redirectTo: {
						name: null,
						params: {
							sku,
							storeId,
							loadCachedPart: false,
						},
					},
				}
			}

			const partScreenData = await loadPartScreenData({
				sku: parseInt(sku, 10) || null,
				storeId,
				useCachedPart: loadCachedPart,
			})

			return {
				...partScreenData,
				tab,
			}
		},
	})
}
